import React from "react";
import FilterIconBlue from "../../resources/filter-blue.svg";
import SearchIconBlue from "../../resources/search-blue.svg";
import XMarkIcon from "../../resources/route-builder/x-mark.svg";

/**
 * Generic search bar with optional filter panel and autocomplete suggestions.
 * Suggestions are presentation-only: { key, label, icon? }. Domain logic lives in the parent.
 * The outlined variant gives compact property pickers the same search behavior.
 */
const SearchBar = (props) => {
  const outlined = props.variant === "outlined";
  const inputRef = React.useRef(null);
  const [showFilters, setShowFilters] = React.useState(
    props.showFiltersDefault ?? window.innerWidth > 1000,
  );
  const [showSuggestions, setShowSuggestions] = React.useState(true);
  // onChange enables live filtering; callers without it search only on submit.
  const isControlled = props.onChange != null;
  const suggestions = props.suggestions ?? [];
  const hasSuggestions = suggestions.length > 0 && showSuggestions;

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    props.onSearch(e.target.search.value);
  };

  const handleSelectSuggestion = (suggestion) => {
    setShowSuggestions(false);
    props.onSelectSuggestion?.(suggestion);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={props.className ?? (outlined ? "mb-0" : "mb-2")}
    >
      <div className="flex flex-row items-center gap-2">
        {/* widthClass allows callers to override the default w-80 width when putting
            the search bar in a narrower container, like the Add Property sidepanel */}
        <div
          className={`relative flex flex-row ${props.widthClass ?? (outlined ? "w-full" : "w-80")} ${
            outlined
              ? "h-7 rounded-lg border border-slate-300 bg-white pr-2 text-slate-600 focus-within:border-sky-500 focus-within:ring-1 focus-within:ring-sky-400"
              : "h-10 rounded-xl my-3 py-1.5 pr-5 text-snow-blue-3 bg-snow-extra-light-grey"
          } ${props.disabled ? "opacity-40" : null}`}
        >
          <button type="submit" aria-label="Search">
            <img
              alt=""
              className={`${outlined ? "h-4 w-4 mx-1.5" : "w-5 h-5 mx-2"} justify-center self-center`}
              src={SearchIconBlue}
            />
          </button>
          <input
            ref={inputRef}
            className={`w-full min-w-0 ${outlined ? "text-xs bg-white text-slate-800" : "text-lg font-light bg-snow-extra-light-grey"} border-none outline-none`}
            type={props.type ?? "text"}
            aria-label={props.label}
            data-testid={props.testId}
            disabled={props.disabled}
            inputMode={props.inputMode || "text"}
            maxLength={props.maxLength}
            placeholder={props.placeholder || "Search..."}
            {...(isControlled
              ? {
                  value: props.value ?? "",
                  onChange: (e) => {
                    setShowSuggestions(true);
                    props.onChange(e.target.value);
                  },
                }
              : { defaultValue: props.value })}
            name="search"
            autoFocus={props.autoFocus ?? true}
            autoComplete="off"
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setShowSuggestions(false);
              }
            }}
          />
          {outlined && isControlled && Boolean(props.value) ? (
            <button
              type="button"
              aria-label="Clear search"
              title="Clear search"
              disabled={props.disabled}
              className="ml-1 flex h-5 w-5 shrink-0 items-center justify-center self-center rounded hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400"
              onClick={() => {
                props.onChange("");
                inputRef.current?.focus();
              }}
            >
              <img src={XMarkIcon} alt="" className="h-3.5 w-3.5" aria-hidden />
            </button>
          ) : null}
          {hasSuggestions ? (
            <ul
              className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-xl bg-white py-1 shadow-lg border border-gray-100"
              role="listbox"
            >
              {suggestions.map((suggestion) => (
                <li key={suggestion.key}>
                  <button
                    type="button"
                    role="option"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-base font-light text-snow-blue-3 hover:bg-snow-extra-light-grey"
                    onMouseDown={(e) => {
                      // Prevent input blur before click registers
                      e.preventDefault();
                    }}
                    onClick={() => handleSelectSuggestion(suggestion)}
                  >
                    {suggestion.icon ? (
                      <span className="shrink-0 flex items-center">
                        {suggestion.icon}
                      </span>
                    ) : null}
                    <span>{suggestion.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        {props.alwaysVisibleFilters ? props.alwaysVisibleFilters : null}
        {props.children ? (
          <button type="button">
            <img
              src={FilterIconBlue}
              alt="filter"
              className="w-5 h-5"
              onClick={() => {
                setShowFilters(!showFilters);
              }}
            />
          </button>
        ) : null}
      </div>
      {props.children ? (
        <div
          style={{ display: showFilters ? "grid" : "none" }}
          className="grid-cols-1 xl:grid-cols-3 grid gap-2"
        >
          {props.children}
        </div>
      ) : null}
    </form>
  );
};

export default SearchBar;
